import { useCallback, useEffect, useRef } from "react";

/**
 * Debounce a callback.
 *
 * Each call restarts the timer, so a burst of input runs the callback once,
 * `delay` ms after it stops. The returned function keeps a **stable identity**
 * across renders (so it is safe in dependency arrays and effect deps), always
 * invokes the latest `callback`, and cancels any pending run on unmount.
 *
 * Replaces the `useRef<ReturnType<typeof setTimeout>>` + `clearTimeout` pattern
 * that was being re-implemented at every call site.
 *
 * @example
 * const search = useDebouncedCallback((term: string) => fetchDefinition(term), 300);
 * <Input onChange={(e) => search(e.target.value)} />
 */
export function useDebouncedCallback<Args extends unknown[]>(
    callback: (...args: Args) => void,
    delay: number,
) {
    // Latest callback in a ref, so the debounced wrapper never needs to be
    // rebuilt when the caller passes an inline function.
    const callbackRef = useRef(callback);
    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Trailing args, so `flush()` can replay the most recent call.
    const lastArgsRef = useRef<Args | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, []);

    const cancel = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        lastArgsRef.current = null;
    }, []);

    const flush = useCallback(() => {
        if (!timerRef.current) return;
        clearTimeout(timerRef.current);
        timerRef.current = null;
        const args = lastArgsRef.current;
        lastArgsRef.current = null;
        if (args) callbackRef.current(...args);
    }, []);

    const isPending = useCallback(() => timerRef.current !== null, []);

    const debounced = useCallback(
        (...args: Args) => {
            lastArgsRef.current = args;
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                lastArgsRef.current = null;
                callbackRef.current(...args);
            }, delay);
        },
        [delay],
    );

    return { debounced, cancel, flush, isPending } as const;
}
